// verifyMagicLink — Phase 6 Task 7 magic link 着地ロジック.
//
// `?action=verify&token=xxx` で着地した際に呼ばれ、DispatcherClient の
// `verifyMagicLink(token)` を実行して purpose ごとの「次の動線」directive
// を返す. 副作用 (localStorage 書き込み / navigate) は呼び出し側 (App の
// `ActionVerifySplash`) が担う — ここはロジックだけ.
//
// 確定方針 (consult 2026-05-04):
// - AC. magic link クリック先は `?action=verify&token=xxx` で web に着地し、
//   web から dispatcher の `GET /auth/verify?token=...` を叩く.
// - AD. 検証成功後の遷移先:
//     - bind_email : `roomId` の room に戻る (pathname `/<roomId>`).
//                    cookie session で email 紐付けは確定済 (server で
//                    `setOwnerEmail` 完了 + Set-Cookie 済).
//     - recovery / room_admin : adminToken を localStorage に保存して
//                    管理画面へ (`/admin`, Task 8 で実装予定).
// - AE/AF はログイン状態表示 / ログアウト導線で Task 8 範囲、本モジュールは関与しない.
//
// 戻り値の navigation directive:
//   - 'navigate-room'  : pathname `/<roomId>` に遷移 (bind_email 経路).
//   - 'navigate-admin' : `/admin` に遷移 (recovery / room_admin 経路).
//   - 'expired' / 'invalid' / 'used' / 'unknown' : エラー画面表示 + 文言.
//
// Phase 6 Task 8 で `navigate-admin` 着地後の画面が `AdminPage` (cross-room
// 管理画面) に置換された (Task 7 までは `AdminPlaceholder` 仮置きだった).

import type {
  DispatcherClient,
  VerifyMagicLinkOutcome,
} from '../infrastructure/dispatcher/DispatcherClient.js';

/**
 * 副作用無しの純粋 directive. 呼び出し側 (`ActionVerifySplash`) が:
 *   - 'navigate-admin' なら adminToken を localStorage に保存して `/admin` に
 *     replaceState (cookie session が cross-room admin の主要 auth、adminToken は
 *     fallback として保存しておく).
 *   - 'navigate-room' なら pathname `/<roomId>` に replaceState.
 *   - エラー系なら ErrorScreen で文言を出す.
 */
export type VerifyMagicLinkDirective =
  | {
      readonly outcome: 'navigate-admin';
      readonly roomId: string;
      /** localStorage に保存する adminToken (recovery / room_admin 経由で発行). */
      readonly adminToken: string;
    }
  | {
      readonly outcome: 'navigate-room';
      readonly roomId: string;
      /** bind_email 経路は server が adminToken を返さないので undefined. */
      readonly adminToken?: undefined;
    }
  | {
      readonly outcome: 'expired';
      readonly message: string;
    }
  | {
      readonly outcome: 'invalid';
      readonly message: string;
    }
  | {
      readonly outcome: 'used';
      readonly message: string;
    }
  | {
      readonly outcome: 'unknown';
      readonly message: string;
    };

/* -----------------------------------------------------------------------------
 * Messages
 * --------------------------------------------------------------------------- */

const EXPIRED_MESSAGE =
  'マジックリンクの有効期限が切れています。もう一度ログイン画面からメールを再送してください。';
const USED_MESSAGE =
  'このマジックリンクは既に使用済みです。もう一度ログイン画面からメールを再送してください。';
const INVALID_MESSAGE = 'マジックリンクが無効です。URL が正しいかご確認ください。';
const UNKNOWN_MESSAGE = 'エラーが発生しました。時間を置いて再度お試しください。';

/* -----------------------------------------------------------------------------
 * Public API
 * --------------------------------------------------------------------------- */

export interface VerifyMagicLinkArgs {
  /** Raw token extracted from `?action=verify&token=xxx`. */
  readonly token: string;
  /** DispatcherClient (実装は web の SessionProvider が公開). */
  readonly dispatcher: Pick<DispatcherClient, 'verifyMagicLink'>;
}

/**
 * Verify a magic-link token and return a navigation directive.
 *
 * - bind_email      → `navigate-room` (cookie session で email 紐付け確定)
 * - recovery        → `navigate-admin` + adminToken
 * - room_admin      → `navigate-admin` + adminToken (Task 8 admin が roomId を活用)
 * - expired/used/invalid/unsupported/unknown → エラー directive
 *
 * 呼び出し側は副作用を実行する:
 *   - `navigate-admin` 着地 → AdminTokenStore.set(roomId, adminToken)
 *     + history.replaceState(`/admin`)
 *   - `navigate-room` 着地  → history.replaceState(pathname `/<roomId>`)
 */
export const verifyMagicLink = async (
  args: VerifyMagicLinkArgs,
): Promise<VerifyMagicLinkDirective> => {
  const out = await args.dispatcher.verifyMagicLink(args.token);
  return mapVerifyOutcome(out);
};

/**
 * Pure mapping from DispatcherClient outcome → directive. Exported for unit
 * tests to exhaust each branch without injecting a fake DispatcherClient.
 */
export const mapVerifyOutcome = (out: VerifyMagicLinkOutcome): VerifyMagicLinkDirective => {
  switch (out.outcome) {
    case 'ok-bind-email':
      return { outcome: 'navigate-room', roomId: out.roomId };
    case 'ok-admin':
      return {
        outcome: 'navigate-admin',
        roomId: out.roomId,
        adminToken: out.adminToken,
      };
    case 'expired':
      return { outcome: 'expired', message: EXPIRED_MESSAGE };
    case 'used':
      return { outcome: 'used', message: USED_MESSAGE };
    case 'invalid':
      return { outcome: 'invalid', message: INVALID_MESSAGE };
    case 'unsupported':
      // signup 等 backend が未対応の purpose. UX 上は invalid と同じ扱いで OK.
      return { outcome: 'invalid', message: INVALID_MESSAGE };
    case 'unknown':
      return { outcome: 'unknown', message: UNKNOWN_MESSAGE };
  }
};
